import type { FC, ReactNode } from 'react'
import type { Settings } from '@/types/content'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { CtaButton } from '@/components/generals/cta-button'

interface MobileCtaBarProps {
    settings: Settings
}

// Keeps the request one tap away on small screens: appears after the first screen,
// steps aside while the closing call to action or the footer is visible.
export const MobileCtaBar: FC<MobileCtaBarProps> = ({ settings }): ReactNode => {
    const [pastIntro, setPastIntro] = useState(false)
    const [endVisible, setEndVisible] = useState(false)

    useEffect(() => {
        const update = () => setPastIntro(window.scrollY > window.innerHeight * 0.75)

        update()
        window.addEventListener('scroll', update, { passive: true })

        const targets = ['abschluss', 'site-footer']
            .map(id => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        const visible = new Set<Element>()

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (entry.isIntersecting) visible.add(entry.target)
                else visible.delete(entry.target)
            }

            setEndVisible(visible.size > 0)
        })

        for (const target of targets) observer.observe(target)

        return () => {
            window.removeEventListener('scroll', update)
            observer.disconnect()
        }
    }, [])

    const shown = pastIntro && !endVisible

    return (
        <div
            aria-hidden={!shown}
            inert={!shown}
            className={cn(
                'fixed inset-x-0 bottom-0 z-30 border-t border-ink/10 bg-bone/90 px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md transition-transform duration-500 ease-brand sm:hidden',
                shown ? 'translate-y-0' : 'translate-y-full'
            )}
        >
            <CtaButton settings={settings} className='w-full' />
        </div>
    )
}
